import type { Lang } from "./i18n.ts";

const nbsp = " ";

function group(n: number): string {
  return Math.round(Math.abs(n))
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, nbsp);
}

/** «1 250 000 сум», «−18 000 so'm», «$12.50». */
export function money(n: number, currency: "UZS" | "USD", lang: Lang, sign = false): string {
  const s = n < 0 ? "−" : sign && n > 0 ? "+" : "";
  if (currency === "USD") {
    const abs = Math.abs(n);
    const body = Number.isInteger(abs) ? group(abs) : `${group(Math.trunc(abs))}.${abs.toFixed(2).split(".")[1]}`;
    return `${s}$${body}`;
  }
  return `${s}${group(n)}${nbsp}${lang === "uz" ? "so'm" : "сум"}`;
}

/** Компактно для подписей графиков: «1,2 млн», «350 тыс.» / «1,2 mln», «350 ming». */
export function compact(n: number, lang: Lang): string {
  const abs = Math.abs(n);
  const s = n < 0 ? "−" : "";
  const fmt = (v: number) => (v >= 10 ? Math.round(v).toString() : (Math.round(v * 10) / 10).toString().replace(".", ","));
  if (abs >= 1_000_000) return `${s}${fmt(abs / 1_000_000)}${nbsp}${lang === "uz" ? "mln" : "млн"}`;
  if (abs >= 1_000) return `${s}${fmt(abs / 1_000)}${nbsp}${lang === "uz" ? "ming" : "тыс."}`;
  return `${s}${Math.round(abs)}`;
}

/** Изменение к прошлому периоду в процентах; null, если сравнивать не с чем. */
export function changePct(current: number, previous: number): number | null {
  if (previous <= 0) return null;
  return Math.round(((current - previous) / previous) * 100);
}

const MONTHS_RU = ["Январь", "Февраль", "Март", "Апрель", "Май", "Июнь", "Июль", "Август", "Сентябрь", "Октябрь", "Ноябрь", "Декабрь"];
const MONTHS_UZ = ["Yanvar", "Fevral", "Mart", "Aprel", "May", "Iyun", "Iyul", "Avgust", "Sentabr", "Oktabr", "Noyabr", "Dekabr"];

export function monthTitle(month: string, lang: Lang): string {
  const [y, m] = month.split("-").map(Number) as [number, number];
  return `${(lang === "uz" ? MONTHS_UZ : MONTHS_RU)[m - 1]} ${y}`;
}

/** Название месяца без года для подписей: «октябрь» / «Oktabr». */
export function monthName(month: string, lang: Lang): string {
  const name = (lang === "uz" ? MONTHS_UZ : MONTHS_RU)[Number(month.slice(5, 7)) - 1] ?? "";
  return lang === "uz" ? name : name.toLowerCase();
}

export function shiftMonth(month: string, delta: number): string {
  const [y, m] = month.split("-").map(Number) as [number, number];
  return new Date(Date.UTC(y, m - 1 + delta, 1)).toISOString().slice(0, 7);
}

/** Текущий месяц по Ташкенту (UTC+5). */
export function currentMonth(now = new Date()): string {
  return new Date(now.getTime() + 5 * 3600 * 1000).toISOString().slice(0, 7);
}

export function shortDate(iso: string): string {
  const d = new Date(new Date(iso).getTime() + 5 * 3600 * 1000);
  return `${String(d.getUTCDate()).padStart(2, "0")}.${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
}

export function dayOfMonth(date: string): number {
  return Number(date.slice(8, 10));
}

const DAY_MS = 24 * 3600 * 1000;
const TZ_MS = 5 * 3600 * 1000;

/** Сегодня по Ташкенту (со сдвигом в днях) — YYYY-MM-DD. */
export function todayIso(offsetDays = 0, now = new Date()): string {
  return new Date(now.getTime() + TZ_MS + offsetDays * DAY_MS).toISOString().slice(0, 10);
}

/** День операции по Ташкенту — YYYY-MM-DD. */
export function localDay(iso: string): string {
  return new Date(new Date(iso).getTime() + TZ_MS).toISOString().slice(0, 10);
}

const MONTHS_GEN_RU = ["января", "февраля", "марта", "апреля", "мая", "июня", "июля", "августа", "сентября", "октября", "ноября", "декабря"];

/** «Сегодня», «Вчера», «Завтра» (для платежей), «3 октября» / «Bugun», «Kecha», «Ertaga», «3-oktabr». */
export function dayTitle(day: string, lang: Lang, now = new Date()): string {
  if (day === todayIso(0, now)) return lang === "uz" ? "Bugun" : "Сегодня";
  if (day === todayIso(-1, now)) return lang === "uz" ? "Kecha" : "Вчера";
  if (day === todayIso(1, now)) return lang === "uz" ? "Ertaga" : "Завтра";
  const [y, m, d] = day.split("-").map(Number) as [number, number, number];
  const sameYear = y === Number(todayIso(0, now).slice(0, 4));
  const title = lang === "uz" ? `${d}-${MONTHS_UZ[m - 1]!.toLowerCase()}` : `${d} ${MONTHS_GEN_RU[m - 1]}`;
  return sameYear ? title : `${title} ${y}`;
}

/** «03.10.2026, 14:35» по Ташкенту. */
export function dateTime(iso: string): string {
  const d = new Date(new Date(iso).getTime() + TZ_MS);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${p(d.getUTCDate())}.${p(d.getUTCMonth() + 1)}.${d.getUTCFullYear()}, ${p(d.getUTCHours())}:${p(d.getUTCMinutes())}`;
}
